import React, {useContext, useState, useRef} from 'react'
import PropTypes from 'prop-types'

import UnavailableProductConfirmationModal from '../components/unavailable-product-confirmation-modal'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useProducts} from '@salesforce/commerce-sdk-react'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'

export const UnavailableProductsInCartContext = React.createContext()

export const useUnavailableProductsInCartContext = () =>
    useContext(UnavailableProductsInCartContext)

export const UnavailableProductsInCartProvider = ({children}) => {
    const UnavailableProductsInCart = useUnavailableProductsInCart()

    const {data: basket} = useCurrentBasket()
    const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket')
    const [isDeletingUnavailableItem, setIsDeletingUnavailableItem] = useState(false)

    const handleRemoveItem = async (product) => {
        await removeItemFromBasketMutation.mutateAsync({
            parameters: {basketId: basket.basketId, itemId: product.itemId}
        })
    }

    const handleUnavailableProducts = async (unavailableProductIds) => {
        setIsDeletingUnavailableItem(true)
        const productItems = basket?.productItems?.filter((item) =>
            unavailableProductIds?.includes(item.productId)
        )
        for (let item of productItems) {
            await handleRemoveItem(item)
        }
        setIsDeletingUnavailableItem(false)
    }

    const productItems = basket?.productItems || []
    const unavailableProductIdsRef = useRef(null)
    const ids = productItems.map((i) => i.productId)
    useProducts(
        {parameters: {ids: ids?.join(','), allImages: true}},
        {
            enabled: ids?.length > 0,
            onSuccess: (result) => {
                const resProductIds = []
                const unOrderableIds = []
                result.data?.forEach(({id, inventory}) => {
                    // when a product is unavailable, the getProducts will not return its product detail.
                    // we compare the response ids with the ones in basket to figure which product has become unavailable
                    resProductIds.push(id)

                    // when a product is orderable, but the quantity in the basket is more than the remaining stock
                    // we want to make sure it is removed before go to checkout page to avoid error when placing order
                    // we don't need to remove low stock/ out of stock from wishlist
                    if (productItems.length) {
                        const productItem = productItems.find((item) => item.productId === id)
                        // wishlist item will have the property type
                        const isWishlist = !!productItem?.type
                        if (
                            !isWishlist &&
                            (!inventory?.orderable ||
                                (inventory?.orderable &&
                                    productItem?.quantity > inventory.stockLevel))
                        ) {
                            unOrderableIds.push(id)
                        }
                    }
                })

                const unavailableProductIds = ids.filter(
                    (id) => !resProductIds.includes(id) || unOrderableIds.includes(id)
                )

                unavailableProductIdsRef.current = unavailableProductIds
            }
        }
    )
    return (
        <UnavailableProductsInCartContext.Provider
            value={{UnavailableProductsInCart, unavailableProductIdsRef}}
        >
            {isDeletingUnavailableItem && <LoadingSpinner wrapperStyles={{height: '100vh'}} />}
            {children}
            <UnavailableProductConfirmationModal
                handleUnavailableProducts={handleUnavailableProducts}
            />
        </UnavailableProductsInCartContext.Provider>
    )
}

UnavailableProductsInCartProvider.propTypes = {
    children: PropTypes.node.isRequired
}

export const useUnavailableProductsInCart = () => {
    const [isOpen, setIsOpen] = useState(false)

    return {
        isOpen,
        onOpen: () => {
            setIsOpen(true)
        },
        onClose: () => {
            setIsOpen(false)
        }
    }
}
